import React, { useState, useEffect } from 'react';
import { PlusCircle, Trash2, Pencil } from 'lucide-react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { MarkdownEditor } from '@/components/blog/MarkdownEditor';

interface BlogGroup {
  id?: string;
  name: string;
  description: string | null;
  sort_order: number;
}

interface BlogPost {
  id?: string;
  title: string;
  content: string;
  excerpt: string;
  author: string;
  read_time: string;
  group_id: string | null;
  image: string;
  created_at?: string;
  group_name?: string;
  date?: string;
}

interface BlogPostsTabProps {
  blogPosts: BlogPost[];
  setBlogPosts: React.Dispatch<React.SetStateAction<BlogPost[]>>;
  blogGroups: BlogGroup[];
  isLoading: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

const BlogPostsTab: React.FC<BlogPostsTabProps> = ({ blogPosts, setBlogPosts, blogGroups, isLoading, setIsLoading }) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedPost, setSelectedPost] = useState<BlogPost | null>(null);
  const [formData, setFormData] = useState<BlogPost>({
    title: '',
    content: '',
    excerpt: '',
    author: '',
    read_time: '',
    group_id: '',
    image: '',
    is_featured: false
  });
  const { toast } = useToast();

  useEffect(() => {
    fetchBlogPosts();
  }, []);

  const fetchBlogPosts = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get('/api/blog-posts');
      const data = response.data;
      const postsArray = Array.isArray(data) ? data : [];
      setBlogPosts(postsArray.map((post: any) => ({
        ...post,
        date: new Date(post.created_at).toLocaleDateString(),
      })));
    } catch (error) {
      console.error('Error fetching blog posts:', error);
      setBlogPosts([]);
      toast({
        title: "Error",
        description: "Failed to load blog posts",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleContentChange = (value: string) => {
    setFormData(prev => ({
      ...prev,
      content: value
    }));
  };

  const handleGroupChange = (value: string) => {
    setFormData(prev => ({
      ...prev,
      group_id: value === "none" ? "" : value
    }));
  };

  const handleAddPost = () => {
    setIsEditMode(false);
    setSelectedPost(null);
    setFormData({
      title: '',
      content: '',
      excerpt: '',
      author: '',
      read_time: '',
      group_id: '',
      image: '',
      is_featured: false
    });
    setIsDialogOpen(true);
  };

  const handleEditPost = (post: BlogPost) => {
    setIsEditMode(true);
    setSelectedPost(post);
    setFormData({
      title: post.title,
      content: post.content || '',
      excerpt: post.excerpt || '',
      author: post.author || '',
      read_time: post.read_time || '',
      group_id: post.group_id || '',
      image: post.image || '',
      is_featured: post.is_featured || false
    });
    setIsDialogOpen(true);
  };

  const handleDeletePost = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    try {
      await axios.delete(`/api/blog-posts/${id}`);
      toast({
        title: "Success",
        description: "Blog post deleted successfully",
      });
      fetchBlogPosts();
    } catch (error) {
      console.error('Error deleting blog post:', error);
      toast({
        title: "Error",
        description: "Failed to delete blog post",
        variant: "destructive"
      });
    }
  };

  const handleSavePost = async () => {
    try {
      if (isEditMode && selectedPost) {
        await axios.put(`/api/blog-posts/${selectedPost.id}`, {
          title: formData.title,
          content: formData.content,
          excerpt: formData.excerpt,
          author: formData.author,
          read_time: formData.read_time,
          group_id: formData.group_id || null,
          image: formData.image,
          is_featured: formData.is_featured
        });
        toast({
          title: "Success",
          description: "Blog post updated successfully",
        });
      } else {
        await axios.post('/api/blog-posts', {
          title: formData.title,
          content: formData.content,
          excerpt: formData.excerpt,
          author: formData.author,
          read_time: formData.read_time,
          group_id: formData.group_id || null,
          image: formData.image,
          is_featured: formData.is_featured
        });
        toast({
          title: "Success",
          description: "Blog post created successfully",
        });
      }
      setIsDialogOpen(false);
      fetchBlogPosts();
    } catch (error) {
      console.error('Error saving blog post:', error);
      toast({
        title: "Error",
        description: "Failed to save blog post",
        variant: "destructive"
      });
    }
  };

  const handleImageUpload = async (file: File) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post('/api/uploads?type=blog', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      const rawData = response.data;
      const imageUrl = rawData.url ?? rawData.data?.url;
      if (imageUrl) {
        const imageMarkdown = `![${file.name}](${imageUrl})`;
        setFormData(prev => ({
          ...prev,
          content: prev.content + '\n\n' + imageMarkdown,
          image: prev.image || imageUrl
        }));
        toast({
          title: "Success",
          description: "Image uploaded successfully",
        });
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      toast({
        title: "Error",
        description: "Failed to upload image",
        variant: "destructive"
      });
    }
  };

  const handleCoverImageUpload = async (file: File) => {
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      
      const response = await axios.post('/api/uploads?type=blog', uploadFormData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      const rawData = response.data;
      const imageUrl = rawData.url ?? rawData.data?.url;
      if (imageUrl) {
        setFormData(prev => ({
          ...prev,
          image: imageUrl
        }));
        toast({
          title: "Success",
          description: "Cover image uploaded successfully",
        });
      }
    } catch (error) {
      console.error('Error uploading cover image:', error);
      toast({
        title: "Error",
        description: "Failed to upload cover image",
        variant: "destructive"
      });
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Blog Management</h1>
        <Button onClick={handleAddPost}>
          <PlusCircle className="mr-2 h-4 w-4" />
          New Post
        </Button>
      </div>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-16">#</TableHead>
              <TableHead>Title</TableHead>
              <TableHead>Author</TableHead>
              <TableHead>Group</TableHead>
              <TableHead>Date</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-4">Loading...</TableCell>
              </TableRow>
            ) : blogPosts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-4">No blog posts found</TableCell>
              </TableRow>
            ) : (
              blogPosts.map((post, index) => (
                <TableRow key={post.id}>
                  <TableCell className="font-medium">{index + 1}</TableCell>
                  <TableCell>{post.title}</TableCell>
                  <TableCell>{post.author}</TableCell>
                  <TableCell>{post.group_name || '-'}</TableCell>
                  <TableCell>{post.date}</TableCell>
                  <TableCell className="text-right space-x-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => handleEditPost(post)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleDeletePost(post.id)}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" aria-describedby="post-dialog-description">
          <DialogHeader>
            <DialogTitle>{isEditMode ? 'Edit Blog Post' : 'Add New Blog Post'}</DialogTitle>
            <DialogDescription id="post-dialog-description">
              Fill in the details for the blog post below.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <label htmlFor="title" className="text-sm font-medium">Title</label>
              <Input
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="Enter blog post title"
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="group_id" className="text-sm font-medium">Group</label>
              <Select onValueChange={handleGroupChange} value={formData.group_id}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select a group" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Group</SelectItem>
                  {blogGroups.map(group => (
                    <SelectItem key={group.id} value={group.id}>{group.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label htmlFor="excerpt" className="text-sm font-medium">Excerpt</label>
              <Input
                id="excerpt"
                name="excerpt"
                value={formData.excerpt}
                onChange={handleInputChange}
                placeholder="Enter blog post excerpt"
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="author" className="text-sm font-medium">Author</label>
              <Input
                id="author"
                name="author"
                value={formData.author}
                onChange={handleInputChange}
                placeholder="Enter author name"
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="read_time" className="text-sm font-medium">Read Time</label>
              <Input
                id="read_time"
                name="read_time"
                value={formData.read_time}
                onChange={handleInputChange}
                placeholder="Enter read time (e.g., 5 min)"
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="image" className="text-sm font-medium">Cover Image</label>
              <div className="flex items-center space-x-2">
                <Input
                  id="image"
                  name="image"
                  value={formData.image}
                  onChange={handleInputChange}
                  placeholder="Enter image URL or upload a file"
                  className="flex-1"
                />
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      handleCoverImageUpload(file);
                    }
                  }}
                  className="hidden"
                  id="cover-image-upload"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => document.getElementById('cover-image-upload')?.click()}
                >
                  Upload
                </Button>
              </div>
              {formData.image && (
                <div className="mt-2">
                  <img
                    src={formData.image}
                    alt="Cover preview"
                    className="w-32 h-20 object-cover rounded border"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                </div>
              )}
            </div>
            <div className="space-y-2">
              <label htmlFor="content" className="text-sm font-medium">Content</label>
              <div className="border rounded-md">
                <MarkdownEditor
                  value={formData.content}
                  onChange={handleContentChange}
                  onImageUpload={handleImageUpload}
                />
              </div>
              <div className="space-y-2">
                <label htmlFor="is_featured" className="text-sm font-medium">Featured</label>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_featured"
                    name="is_featured"
                    checked={formData.is_featured}
                    onChange={handleInputChange}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-500">Show on homepage</span>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter className="sticky bottom-0 bg-background pt-2 pb-2">
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSavePost}>
              {isEditMode ? 'Update' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BlogPostsTab;